import { NETWORK_GRAPH_WORKER_PROTOCOL } from './generated-network-graph';

interface NetworkGraphWorkerEnvelope {
  readonly protocol: typeof NETWORK_GRAPH_WORKER_PROTOCOL;
  readonly requestId: string;
  readonly generation: number;
}

export interface NetworkGraphInitializeRequest extends NetworkGraphWorkerEnvelope {
  readonly type: 'initialize';
  readonly artifactUrl: string;
  readonly catalogVersion: string;
  readonly coreEdgeCount: number;
  readonly edgeCount: number;
  readonly endpointConnectorEdgeCount: number;
  readonly formatVersion: string;
  readonly graphVersion: string;
  readonly schemaVersion: string;
  readonly vertexCount: number;
}

export interface NetworkGraphQueryRequest extends NetworkGraphWorkerEnvelope {
  readonly type: 'query';
  readonly destinationCatalogDocumentId: string;
  readonly originCatalogDocumentId: string;
}

export interface NetworkGraphDisposeRequest extends NetworkGraphWorkerEnvelope {
  readonly type: 'dispose';
}

export type NetworkGraphWorkerRequest =
  NetworkGraphDisposeRequest | NetworkGraphInitializeRequest | NetworkGraphQueryRequest;

export interface NetworkGraphPathEdge {
  readonly graphEdgeId: string;
  readonly lengthMetres: number;
  readonly osmWayId: number;
}

export type NetworkGraphWorkerResponse =
  | (NetworkGraphWorkerEnvelope & {
      readonly type: 'ready';
      readonly graphVersion: string;
      readonly anchoredDocumentCount: number;
      readonly artifactCompressedBytes: number;
      readonly catalogDocumentCount: number;
      readonly coreEdgeCount: number;
      readonly edgeCount: number;
      readonly endpointConnectorEdgeCount: number;
      readonly vertexCount: number;
    })
  | (NetworkGraphWorkerEnvelope & {
      readonly type: 'route';
      readonly graphVersion: string;
      readonly edges: readonly NetworkGraphPathEdge[];
      readonly lengthMetres: number;
    })
  | (NetworkGraphWorkerEnvelope & {
      readonly type: 'no_route';
      readonly graphVersion: string;
    })
  | (NetworkGraphWorkerEnvelope & {
      readonly type: 'ack';
      readonly operation: 'dispose';
    })
  | (NetworkGraphWorkerEnvelope & {
      readonly type: 'error';
      readonly code: 'corrupt' | 'incompatible' | 'invalid_message' | 'unavailable';
      readonly message: string;
    });

export function isNetworkGraphWorkerResponse(value: unknown): value is NetworkGraphWorkerResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'protocol' in value &&
    value.protocol === NETWORK_GRAPH_WORKER_PROTOCOL &&
    'requestId' in value &&
    typeof value.requestId === 'string' &&
    'generation' in value &&
    Number.isSafeInteger(value.generation) &&
    'type' in value &&
    typeof value.type === 'string'
  );
}
